/**
 * THE CFP CASE SHELF — the published cases we run the engine against, and what came out.
 *
 * WHY THESE CASES AND NOT INVENTED HOUSEHOLDS. A household we invent proves nothing: we would have
 * authored both the question and the answer. These are set by the Financial Planning Association
 * for the CFP-track students competing in its Financial Planning Challenge, published openly by the
 * FPA years before this product existed. The reader can open the original in one click and check
 * that we did not choose the facts to suit ourselves. The case is the input; the plan is the output.
 *
 * WHAT WE MAY AND MAY NOT DO WITH THEM (checked 2026-08-09, and the reason the shape of this file
 * is what it is):
 *   * LINK, never host. `caseUrl` points at the FPA's own copy on its own site. We do not copy the
 *     PDF to our CDN, and the local copies under `backend/evals/case_battery/results/source_pdfs/`
 *     are eval inputs that stay internal.
 *   * The synopsis is OURS, written from the facts. Facts are not copyrightable; the case author's
 *     expression is. Never paste the case's own prose here.
 *   * NO FPA MARKS. The 2025 case states it plainly: "The marks may not be used without written
 *     permission from the Financial Planning Association." So the competition is named in plain
 *     text, and no FPA logo, wordmark or badge appears anywhere on this page.
 *   * NO COMPARISON AGAINST THE COMPETITION'S FINALIST PLANS (founder, 2026-08-09). We show our
 *     work beside the question, never beside a student team's answer.
 *
 * WHAT WE MAY NOT CLAIM, which matters more than any of the above. We do NOT say we "solve", "pass"
 * or "win" these cases. Our measured hit rate runs roughly 45-87% depending on case and run, and
 * the judge disagrees with itself on identical text (10/9/11/11/9 on one dump). A published score
 * invites a CFP to verify a number we cannot reliably reproduce — and for a product whose whole
 * claim is that it never fakes a number, an unreproducible marketing statistic is the worst
 * available own goal. The copy therefore shows the artifact and says: read the case, read the plan,
 * judge for yourself. If a figure is ever added here it must carry its method, date and pass count.
 */

export type ShowcaseCase = {
  /** Competition year, and the card's eyebrow. */
  year: string
  /** The household, as the case names them. */
  title: string
  /** One line: who they are and what makes the case hard. */
  standfirst: string
  /** Our own synopsis of the situation — never the case's prose. */
  synopsis: string
  /** What the engine had to compute here. Plain nouns, no scores. */
  demands: string[]
  /** The FPA's own published copy of the case. Link only; never rehosted. */
  caseUrl: string
  /** Share token for the generated living plan, once minted. The card degrades without it. */
  planUrl?: string
  /** The opening of the plan letter the engine wrote, verbatim. Filled from the real run. */
  letter?: string
  /** Set on the case that had never been run before it was published here. */
  cold?: boolean
  /**
   * The computational demonstration, in the order a reader follows it: the household (synopsis above) ->
   * the obvious strategy -> its probability -> what the search found -> the lever -> the improvement ->
   * the assumptions and what is NOT supported. EVERY FIELD IS OPTIONAL AND EMPTY UNTIL A REAL RUN FILLS IT.
   * TODO(founder): fill from a recorded engine run (plan id + date), never from memory or a rounded guess.
   * The panel renders only the fields that are present, so a half-filled demo never shows placeholders.
   */
  demo?: {
    obviousStrategy?: string
    /** e.g. "58% chance the plan is funded" — copied from the run, with the run's own rounding. */
    obviousProbability?: string
    optimizerFound?: string
    lever?: string
    improvement?: string
    assumptions?: string[]
    notSupported?: string[]
  }
}

export const CASE_SHELF: ShowcaseCase[] = [
  {
    year: '2025',
    title: 'Jamie & Aimee Krag',
    standfirst: 'Newlyweds, $422k of income, $467k of student debt, and a sister who will need them.',
    synopsis:
      'A software engineer and a hospital pharmacist, married this year, who have not yet combined '
      + 'their finances. Two student loans at 7% dwarf a net worth built almost entirely from vested '
      + 'stock in three of his employers. They want a house in San Mateo County, four children or '
      + 'possibly one, a ski cabin, and to know whether retiring at 55 is real. Underneath all of it '
      + 'sits Jamie’s sister, who has special needs and whose care he expects to inherit without '
      + 'knowing what that costs.',
    demands: [
      'Concentration: three single stocks are 63% of the portfolio, all in his own industry',
      'Equity compensation: four overlapping RSU awards vesting monthly',
      '7% student debt against an early-retirement goal',
      'A lifetime dependant nobody has costed',
      'A first joint tax year, and a postnuptial question',
    ],
    caseUrl:
      'https://www.financialplanningassociation.org/sites/default/files/2025-02/FPC25%20Case%20Study.pdf',
    cold: true,
  },
  {
    year: '2024',
    title: 'Priya & Bob Johnson',
    standfirst: 'Two college-bound kids, a self-employed spouse with no retirement plan, and family in Mumbai.',
    synopsis:
      'A Santa Clara household four years from an empty nest, on $205k of spending and about $561k '
      + 'of investable assets. Her digital-marketing income swings between $5k and $15k a month with '
      + 'no plan, no disability cover and no life cover; his group life is under one times salary. A '
      + 'trust written in 2012 has never been reviewed, and a standing remittance to Mumbai may grow '
      + 'into eldercare at any time.',
    demands: [
      'Variable self-employment income and a missing retirement plan',
      'Two education paths, one resting on an athletic scholarship',
      'Protection gaps on both lives',
      'An unreviewed trust and cross-border family support',
    ],
    caseUrl:
      'https://www.financialplanningassociation.org/sites/default/files/2025-02/FPC24%20Case%20Study%20Final%20Copy.pdf',
  },
  {
    year: '2023',
    title: 'James & Pamela Rogers',
    standfirst: 'A teacher’s pension that never rises, and a survivor benefit the rules may erase.',
    synopsis:
      'A couple at 58 and 55 asking whether 60 is possible instead of 67. Almost everything is in '
      + 'pre-tax accounts. Her teaching service was not covered by Social Security, so the offset '
      + 'rules decide what she actually receives as a spouse and as a survivor — and his claiming age '
      + 'decides how much of that survives him. A $1M term policy expires in under two years.',
    demands: [
      'Government Pension Offset and the survivor question',
      'A no-COLA pension eroding for thirty years',
      'The pre-Medicare health bridge if he stops at 60',
      'A conversion window between retiring and claiming',
    ],
    caseUrl:
      'https://www.financialplanningassociation.org/sites/default/files/2023-02/FPC23%20Case%20Study%20-%20The%20Rogers%20Retire_FINAL.pdf',
  },
  {
    year: '2022',
    title: 'Sky Jay',
    standfirst: 'A $2m year arriving for an artist with $4,647 in the bank and an unfiled return.',
    synopsis:
      'A 35-year-old installation artist whose income is about to jump by an order of magnitude, '
      + 'with nothing saved for retirement, a 19.5% card balance, federal and private student loans '
      + 'on an income-driven plan that is about to recalculate, and a prior year still unfiled. '
      + 'Everything she wants — a home, her nephews’ education, her parents’ care — depends '
      + 'on what is reserved before the money is spent.',
    demands: [
      'Reserving tax on a windfall before it is spent',
      'Entity structure and self-employment tax',
      'Forgiveness versus payoff on income-driven loans',
      'Concentrated meme-stock risk and no emergency fund',
    ],
    caseUrl:
      'https://www.financialplanningassociation.org/sites/default/files/2022-02/2022%20FPC%20Case%20Study%20Final.pdf',
  },
  {
    year: '2021',
    title: 'Nick Beverly & Krista Conley',
    standfirst: 'A baby due in July, $250k of spending, and 69% of the portfolio in one stock.',
    synopsis:
      'A couple whose income just fell as their outgoings did not, with a child arriving mid-year. '
      + 'A single position is more than two-thirds of the taxable account and carries a $371k '
      + 'embedded gain. He has incentive stock options he does not understand, she has 1099 income '
      + 'with no retirement vehicle attached, and neither has a will.',
    demands: [
      'ISO exercise timing and the alternative minimum tax',
      'Unwinding a 69% position against the gain it carries',
      'Protection before a birth, on both lives',
      'Retirement structure for newly self-employed income',
    ],
    caseUrl:
      'https://www.financialplanningassociation.org/sites/default/files/2021-02/FPC21%20Case%20Study%20Final.pdf',
  },
  {
    year: '2020',
    title: 'Margaret Miner & James John',
    standfirst: 'A $20.8m estate, most of it illiquid, and a daughter whose benefits a gift could end.',
    synopsis:
      'A business owner and a police officer, married late, with a prenuptial agreement and a family '
      + 'business that is both the retirement plan and the estate problem. Nearly $16m of the estate '
      + 'cannot be sold quickly. A daughter with special needs must never be left money outright, and '
      + 'a $2m term policy expires next year.',
    demands: [
      'Estate liquidity against an illiquid business',
      'Succession that also has to fund a retirement',
      'Special-needs planning that preserves benefits',
      'A pension with a survivor election and a prenup to read first',
    ],
    caseUrl:
      'https://www.financialplanningassociation.org/sites/default/files/2020-05/FPC20%20Case%20Study.pdf',
  },
  {
    year: '2019',
    title: 'Jamie & Claire Jackson',
    standfirst: 'Two educations, an 18% card, a 7.75% mortgage, and one surplus that cannot cover them.',
    synopsis:
      'A household whose problem is not any single decision but the order of them. About $14,500 of '
      + 'annual surplus is asked to clear expensive debt, close a retirement gap, fund two educations '
      + 'four and six years out, and pay for a wedding. She has no life cover and no disability '
      + 'cover; 40% of her retirement account is stock in the employer who also pays her.',
    demands: [
      'Sequencing goals that the surplus cannot fund at once',
      'A refinance breakeven against an 18% balance',
      'Employer-stock concentration on top of employment risk',
      'Protection gaps on the second income',
    ],
    caseUrl:
      'https://www.financialplanningassociation.org/sites/default/files/2021-07/FPC%202019%20Case%20Study.pdf',
  },
]

/** Shown under the shelf. Says what these are, whose they are, and what we are not claiming. */
export const CASE_SHELF_NOTE =
  'These case studies are set by the Financial Planning Association for its Financial Planning '
  + 'Challenge and are published by the FPA; each links to the FPA’s own copy. Case studies are '
  + 'the copyright of their authors. MyCIO is not affiliated with, endorsed by, or connected to the '
  + 'Financial Planning Association or CFP Board. We show what our engine worked out, including what '
  + 'it could not model, so you can read the case and judge it yourself — we make no claim to have passed, won or scored in any '
  + 'competition.'
